// Cabecera de página unificada: título, contador, acción principal,
// buscador y filtros (incluida la temporada global de la app).
// Sustituye al antiguo GenericFilterHeader fijo que provocaba solapes.
import React from "react";
import PropTypes from "prop-types";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import { useSeason } from "context/SeasonContext";

const pillSx = (active) => ({
  height: 38,
  borderRadius: "19px",
  fontWeight: 600,
  fontSize: "0.8rem",
  bgcolor: active ? "rgba(251,181,63,0.12)" : "grey.100",
  border: "1.5px solid",
  borderColor: active ? "primary.main" : "grey.300",
  color: active ? "#B45309" : "text.primary",
  transition: "all .15s ease",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover": { bgcolor: active ? "rgba(251,181,63,0.2)" : "grey.200" },
  "& .MuiSelect-icon": { color: active ? "#B45309" : "text.secondary" },
});

export default function PageToolbar({
  title,
  count,
  countLabel,
  action,
  search,
  showSeason = true,
  filters = [],
  viewMode,
  onViewModeChange,
  children,
}) {
  const { temporada, setTemporada, temporadas } = useSeason();

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "grey.200",
        p: { xs: 2, sm: 2.5 },
        mb: 3,
        bgcolor: "background.paper",
      }}
    >
      {/* Fila 1: identidad + acción principal */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        mb={search || filters.length || showSeason ? 2 : 0}
      >
        <Stack direction="row" alignItems="baseline" spacing={1.25} sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            noWrap
            sx={{ fontSize: { xs: "1.05rem", sm: "1.15rem" }, lineHeight: 1.2 }}
          >
            {title}
          </Typography>
          {count !== undefined && (
            <Typography variant="caption" color="text.secondary" fontWeight={600} flexShrink={0}>
              {count} {countLabel}
            </Typography>
          )}
        </Stack>

        {action && (
          <Button
            variant="contained"
            startIcon={action.icon}
            onClick={action.onClick}
            sx={{
              borderRadius: 2,
              px: { xs: 2, sm: 3 },
              whiteSpace: "nowrap",
              flexShrink: 0,
              display: { xs: action.hideOnMobile ? "none" : "inline-flex", sm: "inline-flex" },
            }}
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              {action.label}
            </Box>
            <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
              {action.shortLabel || action.label}
            </Box>
          </Button>
        )}
      </Stack>

      {/* Fila 2: buscador + filtros */}
      {(search || filters.length > 0 || showSeason || viewMode !== undefined) && (
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          {search && (
            <TextField
              size="small"
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder || "Buscar..."}
              sx={{
                minWidth: { md: 260 },
                flexGrow: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "19px",
                  bgcolor: "grey.100",
                  "& fieldset": { borderColor: "grey.300" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: search.value ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => search.onChange("")}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          )}

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              overflowX: "auto",
              pb: { xs: 0.5, md: 0 },
              "&::-webkit-scrollbar": { height: 4 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.15)",
                borderRadius: 2,
              },
            }}
          >
            {showSeason && (
              <Tooltip title="Temporada activa en toda la aplicación" placement="top">
                <FormControl sx={{ minWidth: 132, flexShrink: 0 }}>
                  <Select
                    value={temporadas.includes(temporada) ? temporada : ""}
                    onChange={(e) => setTemporada(e.target.value)}
                    IconComponent={KeyboardArrowDownIcon}
                    displayEmpty
                    renderValue={(v) =>
                      v ? (
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <EventRepeatIcon sx={{ fontSize: 16 }} />
                          <span>{`${v}/${String(Number(v) + 1).slice(-2)}`}</span>
                        </Stack>
                      ) : (
                        "Temporada"
                      )
                    }
                    sx={pillSx(true)}
                  >
                    {temporadas.map((t) => (
                      <MenuItem key={t} value={t}>
                        Temporada {t}/{String(Number(t) + 1).slice(-2)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Tooltip>
            )}

            {filters.map((f) => (
              <FormControl key={f.id} sx={{ minWidth: f.minWidth || 110, flexShrink: 0 }}>
                <Select
                  value={f.value}
                  onChange={(e) => f.onChange(e.target.value)}
                  displayEmpty
                  IconComponent={KeyboardArrowDownIcon}
                  renderValue={(selected) => {
                    if (selected === "" || selected === undefined || selected === null) {
                      return f.label;
                    }
                    const opt = f.options.find((o) => String(o.value) === String(selected));
                    return opt ? opt.label : String(selected);
                  }}
                  sx={pillSx(f.value !== "" && f.value !== undefined && f.value !== null)}
                >
                  <MenuItem value="">{f.allLabel || `${f.label}: todos`}</MenuItem>
                  {f.options.map((o) => (
                    <MenuItem key={String(o.value)} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}

            {viewMode !== undefined && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, v) => v && onViewModeChange(v)}
                size="small"
                sx={{
                  flexShrink: 0,
                  ml: "auto",
                  "& .MuiToggleButton-root": {
                    borderRadius: "12px !important",
                    px: 1.25,
                    border: "1.5px solid",
                    borderColor: "grey.300",
                    color: "text.secondary",
                    "&.Mui-selected": {
                      bgcolor: "primary.main",
                      color: "#fff",
                      "&:hover": { bgcolor: "primary.focus" },
                    },
                  },
                }}
              >
                <ToggleButton value="card" aria-label="vista tarjetas">
                  <ViewModuleIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="list" aria-label="vista lista">
                  <ViewListIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
        </Stack>
      )}

      {children}
    </Paper>
  );
}

PageToolbar.propTypes = {
  icon: PropTypes.elementType,
  title: PropTypes.string.isRequired,
  count: PropTypes.number,
  countLabel: PropTypes.string,
  action: PropTypes.shape({
    label: PropTypes.string,
    shortLabel: PropTypes.string,
    icon: PropTypes.node,
    onClick: PropTypes.func,
    hideOnMobile: PropTypes.bool,
  }),
  search: PropTypes.shape({
    value: PropTypes.string,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
  }),
  showSeason: PropTypes.bool,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      allLabel: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      onChange: PropTypes.func.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          label: PropTypes.string,
        })
      ),
      minWidth: PropTypes.number,
    })
  ),
  viewMode: PropTypes.string,
  onViewModeChange: PropTypes.func,
  children: PropTypes.node,
};
